import { useState, useEffect, useCallback, useMemo } from "react";
import { Printer, Wifi, WifiOff, Check, Pencil, EyeOff, Eye, Download, ChevronLeft, ChevronRight, ChevronDown, Monitor } from "lucide-react";
import { useTranslation } from "../i18n";
import { toast } from "react-toastify";
import { PRINT_AGENT_DOWNLOAD_URL, resolvePrintAgentDownloadUrl, getPrintAgentStatus, isAgentOutdated, isPiAgent } from "../components/helpers/printAgent";
import FlowLoader from "../components/FlowLoader";
import AdminPageHeader from "../components/admin-page-header";
import FilterPills from "../components/admin/FilterPills";
import AgentNetworkModal, { AgentNetInfo, AgentCommandPublic, AgentCommandResult } from "../components/admin/AgentNetworkModal";
import { apiFetch } from "../api/apiFetch";
import { useReadOnly } from "../access/ReadOnly";
import { useWarehouseScope } from "../access/useWarehouseScope";
import "../styles/admin-printers.css";

interface PrinterData {
    _id: string;
    AgentId: string;
    SystemName: string;
    FriendlyName: string;
    Warehouse: string;
    Hostname: string;
    Status: "online" | "offline";
    MobileEnabled: boolean;
    Hidden: boolean;
    LastSeen: string;
    // Agent build version reported on registration (agents ≥ 1.4.0).
    // null = pre-1.4.0 agent → needs the last manual installer rollout.
    AgentVersion?: string | null;
}

// One row per MACHINE running the print agent — the device-level rollout
// view (a device shows here even if it exposes no printers).
interface AgentData {
    _id: string;
    AgentId: string;
    Hostname: string;
    Warehouse: string;
    Status: "online" | "offline";
    Version?: string | null;
    LastSeen: string;
    PrinterCount: number;
    // Raspberry Pi agents only — the remote control channel (see
    // AgentNetworkModal): what the device last reported about itself, the
    // command waiting for it, and the outcome of the last one.
    NetInfo?: AgentNetInfo | null;
    PendingCommand?: AgentCommandPublic | null;
    CommandResult?: AgentCommandResult | null;
}

const PAGE_SIZE = 25;

const AdminPrinters = () => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [printers, setPrinters] = useState<PrinterData[]>([]);
    const [agents, setAgents] = useState<AgentData[]>([]);
    const [showAgents, setShowAgents] = useState(false);
    const [loading, setLoading] = useState(true);
    // Installer link — staging FE (newest build) when reachable, else this origin.
    const [downloadUrl, setDownloadUrl] = useState(PRINT_AGENT_DOWNLOAD_URL);
    useEffect(() => { resolvePrintAgentDownloadUrl().then(setDownloadUrl); }, []);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editName, setEditName] = useState("");
    const warehouseScope = useWarehouseScope();
    const [pickedWarehouse, setWarehouseFilter] = useState<string>("");
    // Without cross_warehouse the list is pinned to the person's own warehouse.
    const warehouseFilter = !warehouseScope.canCross && warehouseScope.own ? warehouseScope.own : pickedWarehouse;
    const [showHidden, setShowHidden] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(0);
    const [localHostname, setLocalHostname] = useState<string | null>(
        () => localStorage.getItem("admin-printers-my-computer")
    );

    useEffect(() => {
        getPrintAgentStatus().then(s => {
            if (s && s.hostname) {
                setLocalHostname(s.hostname);
                localStorage.setItem("admin-printers-my-computer", s.hostname);
            }
        });
    }, []);

    useEffect(() => { setPage(0); }, [searchTerm, warehouseFilter, showHidden]);

    const saveHostname = (value: string | null) => {
        if (value) {
            setLocalHostname(value);
            localStorage.setItem("admin-printers-my-computer", value);
        } else {
            setLocalHostname(null);
            localStorage.removeItem("admin-printers-my-computer");
        }
    };

    const setMyComputer = async () => {
        const status = await getPrintAgentStatus();
        if (status && status.hostname) {
            saveHostname(status.hostname);
            return;
        }
        const entered = window.prompt(t("enter_computer_name"), localHostname || "");
        if (entered === null) return;
        saveHostname(entered.trim() || null);
    };

    const fetchPrinters = useCallback(async () => {
        try {
            const res = await apiFetch(`/printers/admin?showHidden=${showHidden}`, {
            });
            if (res.ok) {
                const data = await res.json();
                setPrinters(Array.isArray(data) ? data : []);
            }
        } catch {
            // API may not be available yet — show empty state
        }
        setLoading(false);
    }, [showHidden]);

    useEffect(() => { fetchPrinters(); }, [fetchPrinters]);

    // Device-level agent list — drives the rollout view (every machine with
    // the agent installed + its build version, even if it has no printers).
    const refreshAgents = useCallback(async () => {
        try {
            const res = await apiFetch(`/printers/agents`, {});
            if (res.ok) {
                const data = await res.json();
                setAgents(Array.isArray(data) ? data : []);
            }
        } catch { /* section simply stays empty */ }
    }, []);
    useEffect(() => { refreshAgents(); }, [refreshAgents]);

    // Network & device modal for a Raspberry Pi agent. Everything in it is
    // queued on the BE and collected by the device's ~15s control poll, so
    // the modal just renders the agent row and polls faster while open.
    const [netAgentId, setNetAgentId] = useState<string | null>(null);
    const netAgent = agents.find(a => a.AgentId === netAgentId) ?? null;
    useEffect(() => {
        if (!netAgentId) return;
        const id = setInterval(refreshAgents, 3000);
        return () => clearInterval(id);
    }, [netAgentId, refreshAgents]);
    const queueAgentCommand = async (body: Record<string, unknown>) => {
        if (!netAgentId) return;
        try {
            const res = await apiFetch(`/printers/agents/command`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ agentId: netAgentId, ...body }),
            });
            if (!res.ok) {
                const d = await res.json().catch(() => null);
                toast.error(d?.error ?? d?.message ?? t("server_error"), { toastId: "agent-cmd-err" });
                return;
            }
            await refreshAgents();
        } catch {
            toast.error(t("server_error"), { toastId: "agent-cmd-err" });
        }
    };

    const updatePrinter = async (printerId: string, updates: { friendlyName?: string; mobileEnabled?: boolean; warehouse?: string; hidden?: boolean }) => {
        try {
            const res = await apiFetch(`/printers/update`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ printerId, ...updates }),
            });
            if (res.ok) {
                setPrinters(prev => prev.map(p =>
                    p._id === printerId
                        ? { ...p, ...(updates.friendlyName !== undefined ? { FriendlyName: updates.friendlyName } : {}), ...(updates.mobileEnabled !== undefined ? { MobileEnabled: updates.mobileEnabled } : {}) }
                        : p
                ));
            } else {
                toast.error(t("error"));
            }
        } catch {
            toast.error(t("error"));
        }
    };

    const startEdit = (printer: PrinterData) => {
        setEditingId(printer._id);
        setEditName(printer.FriendlyName || printer.SystemName);
    };

    const saveEdit = (printerId: string) => {
        if (editName.trim()) {
            updatePrinter(printerId, { friendlyName: editName.trim() });
        }
        setEditingId(null);
    };

    const toggleMobile = (printer: PrinterData) => {
        updatePrinter(printer._id, { mobileEnabled: !printer.MobileEnabled });
    };

    // Visibility filter mirrors what was fetched — fetchPrinters is keyed
    // on showHidden so the server already filtered, but we keep this guard
    // for stale state during a flip.
    const hiddenFiltered = printers.filter(p => showHidden ? !!p.Hidden : !p.Hidden);

    const needle = searchTerm.trim().toLowerCase();
    const searchFiltered = needle
        ? hiddenFiltered.filter(p =>
            (p.FriendlyName || "").toLowerCase().includes(needle) ||
            (p.SystemName || "").toLowerCase().includes(needle) ||
            (p.Hostname || "").toLowerCase().includes(needle)
        )
        : hiddenFiltered;

    const warehouseCounts = useMemo(() => {
        const out: Record<string, number> = {};
        for (const p of searchFiltered) if (p.Warehouse) out[p.Warehouse] = (out[p.Warehouse] ?? 0) + 1;
        return out;
    }, [searchFiltered]);

    const warehouses = useMemo(
        () => Object.keys(warehouseCounts).sort().filter(w => warehouseScope.canCross || w === warehouseScope.own),
        [warehouseCounts, warehouseScope.canCross, warehouseScope.own],
    );

    const filtered = warehouseFilter === ""
        ? searchFiltered
        : searchFiltered.filter(p => p.Warehouse === warehouseFilter);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages - 1);
    const sliceStart = safePage * PAGE_SIZE;
    const sliceEnd = sliceStart + PAGE_SIZE;
    const visible = filtered.slice(sliceStart, sliceEnd);
    const rangeFrom = filtered.length === 0 ? 0 : sliceStart + 1;
    const rangeTo = Math.min(sliceEnd, filtered.length);

    const hidePrinter = async (printerId: string) => {
        try {
            const res = await apiFetch(`/printers/hide`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ printerId }),
            });
            if (res.ok) {
                fetchPrinters();
            } else {
                toast.error(`${t("error")} (${res.status})`);
            }
        } catch {
            toast.error(t("error"));
        }
    };

    const unhidePrinter = async (printerId: string) => {
        try {
            const res = await apiFetch(`/printers/unhide`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ printerId }),
            });
            if (res.ok) {
                fetchPrinters();
            } else {
                toast.error(`${t("error")} (${res.status})`);
            }
        } catch {
            toast.error(t("error"));
        }
    };

    return (
        <div className="page-data page-data-wide">
            <AdminPageHeader
                titleKey="printers"
                backTo="/admin/hardware"
                hideSearch
                crumbs={[
                    { labelKey: "admin_panel", to: "/admin" },
                    { labelKey: "hardware", to: "/admin/hardware" },
                    { labelKey: "printers" },
                ]}
                actions={
                    <a
                        href={downloadUrl}
                        className="btn btn-ghost"
                        style={{ textDecoration: "none", fontSize: "0.8rem", display: "flex", alignItems: "center", gap: 6, padding: "0 12px", height: 36, boxSizing: "border-box" }}
                    >
                        <Download size={16} />
                        {t("download_print_agent")}
                    </a>
                }
            />

            {loading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
                    <FlowLoader />
                </div>
            ) : (
                <div className="users-admin">
                    <div className="locations-summary">
                        <div className="locations-summary-total">
                            <span className="locations-summary-total-label">{t("printers")}</span>
                            <strong>{printers.length}</strong>
                        </div>
                        <div className="locations-summary-warehouses">
                            <div className="locations-summary-pill">
                                <span className="locations-summary-pill-label">{t("status_online")}</span>
                                <span className="locations-summary-pill-value">
                                    {printers.filter(p => p.Status === "online").length}
                                </span>
                            </div>
                            <div className="locations-summary-pill">
                                <span className="locations-summary-pill-label">{t("printer_offline")}</span>
                                <span className="locations-summary-pill-value">
                                    {printers.filter(p => p.Status !== "online").length}
                                </span>
                            </div>
                            <div className="locations-summary-pill">
                                <span className="locations-summary-pill-label">{t("mobile_enabled")}</span>
                                <span className="locations-summary-pill-value">
                                    {printers.filter(p => p.MobileEnabled).length}
                                </span>
                            </div>
                            <div className="locations-summary-pill">
                                <span className="locations-summary-pill-label">{t("agents_needing_update")}</span>
                                <span className="locations-summary-pill-value">
                                    {agents.filter(a => isAgentOutdated(a.Version ?? undefined)).length}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Device-level agent list — every machine with the print
                        agent installed, its build version, and when it last
                        checked in. This is the rollout checklist: amber chips
                        are the machines still needing the manual installer. */}
                    <button
                        type="button"
                        className="admin-printers-agents-toggle"
                        onClick={() => setShowAgents(v => !v)}
                    >
                        <Monitor size={14} />
                        {t("agent_devices")} ({agents.length})
                        <ChevronDown
                            size={14}
                            style={{ transition: "transform 0.15s", transform: showAgents ? "rotate(180deg)" : "none" }}
                        />
                    </button>
                    {showAgents && (
                        <div className="locations-table-wrap" style={{ marginBottom: 16 }}>
                            <table className="locations-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 36 }} aria-label="" />
                                        <th>{t("hostname")}</th>
                                        <th style={{ width: 150 }}>{t("warehouse")}</th>
                                        <th style={{ width: 130 }}>{t("agent_version")}</th>
                                        <th style={{ width: 90 }}>{t("printers")}</th>
                                        <th style={{ width: 180 }}>{t("last_seen")}</th>
                                        <th style={{ width: 48 }} aria-label="" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {agents.map(agent => (
                                        <tr key={agent._id}>
                                            <td>
                                                <span
                                                    className={`admin-printers-status-dot admin-printers-status-dot--${agent.Status === "online" ? "online" : "offline"}`}
                                                    title={agent.Status === "online" ? t("status_online") : t("printer_offline")}
                                                >
                                                    {agent.Status === "online" ? <Wifi size={12} /> : <WifiOff size={12} />}
                                                </span>
                                            </td>
                                            <td style={{ fontWeight: 600 }}>{agent.Hostname || "—"}</td>
                                            <td style={{ color: "var(--color-text-muted, #6b7280)", fontSize: "0.82rem" }}>
                                                {agent.Warehouse || "—"}
                                            </td>
                                            <td>
                                                {isAgentOutdated(agent.Version ?? undefined) ? (
                                                    <span className="admin-printers-agent-chip admin-printers-agent-chip--outdated" title={t("agent_update_required")}>
                                                        {agent.Version ? `v${agent.Version}` : t("agent_update_required")}
                                                    </span>
                                                ) : (
                                                    <span className="admin-printers-agent-chip admin-printers-agent-chip--ok">
                                                        v{agent.Version}
                                                    </span>
                                                )}
                                            </td>
                                            <td>{agent.PrinterCount}</td>
                                            <td style={{ color: "var(--color-text-muted, #6b7280)", fontSize: "0.82rem" }}>
                                                {agent.LastSeen ? new Date(agent.LastSeen).toLocaleString() : "—"}
                                            </td>
                                            <td>
                                                {/* Raspberry Pi agents carry a remote network / device channel. */}
                                                {isPiAgent(agent.Version) && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary btn-sm"
                                                        title={t("agent_network")}
                                                        aria-label={t("agent_network")}
                                                        onClick={() => setNetAgentId(agent.AgentId)}
                                                    >
                                                        <Wifi size={14} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {netAgent && (
                        <AgentNetworkModal
                            agentId={netAgent.AgentId}
                            online={netAgent.Status === "online"}
                            netInfo={netAgent.NetInfo ?? null}
                            pending={netAgent.PendingCommand ?? null}
                            result={netAgent.CommandResult ?? null}
                            readOnly={readOnly}
                            onQueue={queueAgentCommand}
                            onClose={() => setNetAgentId(null)}
                        />
                    )}

                    <FilterPills
                        value={warehouseFilter}
                        onChange={setWarehouseFilter}
                        allCount={searchFiltered.length}
                        hideAll={!warehouseScope.canCross}
                        options={warehouses.map(w => ({ value: w, label: w, count: warehouseCounts[w] }))}
                    />

                    <div className="locations-table-filters">
                        <input
                            type="search"
                            className="locations-table-search"
                            placeholder={t("search_printers")}
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                        {localHostname ? (
                            <div className="admin-printers-search-chip-group">
                                <button
                                    type="button"
                                    className="admin-printers-search-chip"
                                    onClick={() => setSearchTerm(localHostname)}
                                    title={t("search_this_computer")}
                                >
                                    {localHostname}
                                </button>
                                <button
                                    type="button"
                                    className="admin-printers-search-chip-edit"
                                    onClick={setMyComputer}
                                    title={t("edit")}
                                >
                                    <Pencil size={12} />
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                className="admin-printers-search-chip admin-printers-search-chip--set"
                                onClick={setMyComputer}
                                title={t("enter_computer_name")}
                            >
                                {t("set_my_computer")}
                            </button>
                        )}
                        <button
                            type="button"
                            className={`admin-printers-hidden-btn ${showHidden ? "admin-printers-hidden-btn--active" : ""}`}
                            onClick={() => setShowHidden(!showHidden)}
                        >
                            {showHidden ? <Eye size={14} /> : <EyeOff size={14} />}
                            {showHidden ? t("hide_hidden") : t("show_hidden")}
                        </button>
                    </div>

                    {filtered.length === 0 ? (
                        <div className="locations-table-empty">
                            <Printer size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
                            <div>{t("no_printers_registered")}</div>
                        </div>
                    ) : (
                        <>
                            <div className="locations-table-wrap">
                                <table className="locations-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 36 }} aria-label="" />
                                            <th>{t("friendly_name")}</th>
                                            <th>{t("system_name")}</th>
                                            <th style={{ width: 160 }}>{t("hostname")}</th>
                                            <th style={{ width: 110 }}>{t("agent_version")}</th>
                                            <th style={{ width: 150 }}>{t("warehouse")}</th>
                                            <th style={{ width: 110 }}>{t("mobile_enabled")}</th>
                                            <th style={{ width: 110, textAlign: "right" }} aria-label="" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visible.map(printer => (
                                            <tr key={printer._id}>
                                                <td>
                                                    <span
                                                        className={`admin-printers-status-dot admin-printers-status-dot--${printer.Status === "online" ? "online" : "offline"}`}
                                                        title={printer.Status === "online" ? t("status_online") : t("printer_offline")}
                                                    >
                                                        {printer.Status === "online" ? <Wifi size={12} /> : <WifiOff size={12} />}
                                                    </span>
                                                </td>
                                                <td>
                                                    {editingId === printer._id ? (
                                                        <div className="admin-printers-inline-edit">
                                                            <input
                                                                className="locations-table-input"
                                                                value={editName}
                                                                onChange={e => setEditName(e.target.value)}
                                                                onKeyDown={e => e.key === "Enter" && saveEdit(printer._id)}
                                                                autoFocus
                                                            />
                                                            <button
                                                                type="button"
                                                                className="locations-table-btn locations-table-btn--primary"
                                                                onClick={() => saveEdit(printer._id)}
                                                                disabled={readOnly}
                                                                aria-disabled={readOnly}
                                                                title={readOnly ? t("read_only_mode") : t("save")}
                                                            >
                                                                <Check size={14} />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <span style={{ fontWeight: 600 }}>
                                                            {printer.FriendlyName || printer.SystemName}
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ color: "var(--color-text-muted, #6b7280)", fontSize: "0.82rem" }}>
                                                    {printer.SystemName}
                                                </td>
                                                <td style={{ color: "var(--color-text-muted, #6b7280)", fontSize: "0.82rem" }}>
                                                    {printer.Hostname || "—"}
                                                </td>
                                                <td>
                                                    {isAgentOutdated(printer.AgentVersion ?? undefined) ? (
                                                        <span className="admin-printers-agent-chip admin-printers-agent-chip--outdated" title={t("agent_update_required")}>
                                                            {printer.AgentVersion ? `v${printer.AgentVersion}` : t("agent_update_required")}
                                                        </span>
                                                    ) : (
                                                        <span className="admin-printers-agent-chip admin-printers-agent-chip--ok">
                                                            v{printer.AgentVersion}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <select
                                                        className="locations-table-input"
                                                        value={printer.Warehouse}
                                                        disabled={readOnly}
                                                        aria-disabled={readOnly}
                                                        title={readOnly ? t("read_only_mode") : undefined}
                                                        onChange={e => {
                                                            const val = e.target.value;
                                                            updatePrinter(printer._id, { warehouse: val });
                                                            setPrinters(prev => prev.map(p => p._id === printer._id ? { ...p, Warehouse: val } : p));
                                                        }}
                                                    >
                                                        <option value="Unassigned">{t("select")}...</option>
                                                        <option value="Pembroke">{t("pembroke_dock")}</option>
                                                        <option value="Newport">Newport</option>
                                                        <option value="Poland">Poland</option>
                                                    </select>
                                                </td>
                                                <td>
                                                    <div
                                                        className={`dispatch-toggle ${printer.MobileEnabled ? "dispatch-toggle--on" : ""} ${(printer.Warehouse === "Unassigned" || readOnly) ? "dispatch-toggle--disabled" : ""}`}
                                                        onClick={() => {
                                                            if (readOnly) return;
                                                            if (printer.Warehouse === "Unassigned") {
                                                                toast.dismiss();
                                                                toast.error(t("select_warehouse_first"));
                                                                return;
                                                            }
                                                            toggleMobile(printer);
                                                        }}
                                                        aria-disabled={readOnly}
                                                        title={readOnly ? t("read_only_mode") : undefined}
                                                        style={{ cursor: (printer.Warehouse === "Unassigned" || readOnly) ? "not-allowed" : "pointer" }}
                                                    >
                                                        <div className="dispatch-toggle-knob" />
                                                    </div>
                                                </td>
                                                <td className="locations-table-actions">
                                                    <button
                                                        type="button"
                                                        className="locations-table-btn"
                                                        onClick={() => startEdit(printer)}
                                                        disabled={readOnly}
                                                        aria-disabled={readOnly}
                                                        aria-label={t("edit")}
                                                        title={readOnly ? t("read_only_mode") : t("edit")}
                                                    >
                                                        <Pencil size={14} />
                                                    </button>
                                                    {printer.Hidden ? (
                                                        <button
                                                            type="button"
                                                            className="locations-table-btn"
                                                            onClick={() => unhidePrinter(printer._id)}
                                                            disabled={readOnly}
                                                            aria-disabled={readOnly}
                                                            aria-label={t("unhide_printer")}
                                                            title={readOnly ? t("read_only_mode") : t("unhide_printer")}
                                                        >
                                                            <Eye size={14} />
                                                        </button>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="locations-table-btn locations-table-btn--delete"
                                                            onClick={() => hidePrinter(printer._id)}
                                                            disabled={readOnly}
                                                            aria-disabled={readOnly}
                                                            aria-label={t("hide_printer")}
                                                            title={readOnly ? t("read_only_mode") : t("hide_printer")}
                                                        >
                                                            <EyeOff size={14} />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="locations-table-footer">
                                <span>{t("locations_showing_range", {
                                    from: String(rangeFrom),
                                    to: String(rangeTo),
                                    total: String(filtered.length),
                                })}</span>
                                <div className="locations-table-pagination">
                                    <button
                                        type="button"
                                        className="locations-table-page-btn"
                                        onClick={() => setPage(p => Math.max(0, p - 1))}
                                        disabled={safePage === 0}
                                        aria-label={t("pagination_previous")}
                                    >
                                        <ChevronLeft size={16} />
                                        <span>{t("pagination_previous")}</span>
                                    </button>
                                    <span className="locations-table-page-indicator">
                                        {t("pagination_page_x_of_y", {
                                            page: String(safePage + 1),
                                            total: String(totalPages),
                                        })}
                                    </span>
                                    <button
                                        type="button"
                                        className="locations-table-page-btn"
                                        onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                        disabled={safePage >= totalPages - 1}
                                        aria-label={t("pagination_next")}
                                    >
                                        <span>{t("pagination_next")}</span>
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default AdminPrinters;
